import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { UserMinus02Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface RemoveUserDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the user to remove.
   */
  userId: string | null;
  /**
   * The email address of the user to remove (for display purposes).
   */
  userEmail?: string;
  /**
   * Callback when the user is removed.
   */
  onRemove: (id: string) => Promise<void>;
  /**
   * The name of the organization the user is being removed from.
   */
  organizationName: string;
}

function RemoveUserDialog(props: RemoveUserDialogProps) {
  const { isOpen = false, onOpenChange, onRemove, userId, userEmail, organizationName, ...other } = props;

  const handleOnRemove = React.useCallback(async () => {
    if (userId) {
      await onRemove?.(userId);
    }
  }, [onRemove, userId]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content className="w-[400px] max-[320px]:w-full" dialogClassName="gap-8 max-w-[600px]">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={UserMinus02Icon}>
            <Dialog.Title showEllipsis={true} className="text-md">
              Remove user from {organizationName}
            </Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              Are you sure you want to remove {userEmail ?? 'this user'} from {organizationName}? They will lose access
              to all data in this organization.
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer>
            <Button className="w-full" size={ButtonSize.Lg} level={ButtonLevel.Secondary} onPress={handleClose}>
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnRemove}
            >
              Remove
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { RemoveUserDialog };
